import React, { useState, useEffect } from 'react';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, 
  Clock, Eye, CheckCircle, AlertTriangle,
  FileText, Users, Target
} from 'lucide-react';

const EditorialCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // month, week
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cms_token') || 'editor-user-token';
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/cms/calendar?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCalendarData(data);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDate = (day) => {
    if (!calendarData || !day) return [];
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const events = [];
    
    // Scheduled content
    calendarData.scheduled_content?.forEach(schedule => {
      const scheduleDate = new Date(schedule.publish_at).toISOString().split('T')[0];
      if (scheduleDate === dateStr) {
        events.push({
          type: 'scheduled',
          title: `Publish: Walk ${schedule.walk_id}`,
          time: new Date(schedule.publish_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          data: schedule
        });
      }
    });
    
    // Content in review
    calendarData.content_in_review?.forEach(walk => {
      const reviewDate = new Date(walk.updated_at).toISOString().split('T')[0];
      if (reviewDate === dateStr) {
        events.push({
          type: 'review',
          title: `Review: ${walk.title}`,
          time: 'Pending',
          data: walk
        });
      }
    });
    
    return events;
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'review': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'published': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'scheduled': return Clock;
      case 'review': return Eye;
      case 'published': return CheckCircle;
      default: return FileText;
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const CalendarDay = ({ day }) => {
    const events = getEventsForDate(day);
    const dayIsToday = isToday(day);

    return (
      <div
        className={`min-h-24 p-2 border border-gray-200 ${
          day ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
        } ${dayIsToday ? 'ring-2 ring-blue-500' : ''}`}
        onClick={() => day && setSelectedDate(day)}
      >
        {day && (
          <>
            <div className={`text-sm font-medium mb-1 ${
              dayIsToday ? 'text-blue-600' : 'text-gray-900'
            }`}>
              {day}
            </div>
            <div className="space-y-1">
              {events.slice(0, 2).map((event, index) => {
                const Icon = getEventIcon(event.type);
                return (
                  <div
                    key={index}
                    className={`text-xs p-1 rounded border ${getEventColor(event.type)} truncate`}
                  >
                    <div className="flex items-center">
                      <Icon className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{event.title}</span>
                    </div>
                  </div>
                );
              })}
              {events.length > 2 && (
                <div className="text-xs text-gray-500">
                  +{events.length - 2} more
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const CalendarStats = () => {
    if (!calendarData) return null;

    const stats = [
      {
        label: 'Scheduled',
        value: calendarData.scheduled_content?.length || 0,
        icon: Clock,
        color: 'text-blue-600'
      },
      {
        label: 'In Review',
        value: calendarData.content_in_review?.length || 0,
        icon: Eye,
        color: 'text-yellow-600'
      },
      {
        label: 'Events',
        value: calendarData.calendar_events?.length || 0,
        icon: Target,
        color: 'text-purple-600'
      }
    ];

    return (
      <div className="grid grid-cols-3 gap-4 mb-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <stat.icon className={`w-5 h-5 ${stat.color} mr-2`} />
              <div>
                <div className="text-lg font-semibold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Editorial Calendar</h3>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h4 className="text-lg font-medium text-gray-900 min-w-40 text-center">
                {formatDate(currentDate)}
              </h4>
              <button
                onClick={() => navigateMonth(1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading calendar...</p>
          </div>
        ) : (
          <>
            <CalendarStats />
            
            {/* Calendar Grid */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Week day headers */}
              <div className="grid grid-cols-7 bg-gray-50">
                {weekDays.map((day) => (
                  <div key={day} className="p-3 text-sm font-medium text-gray-700 text-center border-r border-gray-200 last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7">
                {days.map((day, index) => (
                  <CalendarDay key={index} day={day} />
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-6 flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded mr-2"></div>
                <span className="text-gray-600">Scheduled</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-100 border border-yellow-200 rounded mr-2"></div>
                <span className="text-gray-600">In Review</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-100 border border-green-200 rounded mr-2"></div>
                <span className="text-gray-600">Published</span>
              </div>
            </div>

            {/* Selected Date Details */}
            {selectedDate && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="font-medium text-blue-900 mb-2">
                  {formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), selectedDate))}
                </h5>
                <div className="space-y-2">
                  {getEventsForDate(selectedDate).map((event, index) => {
                    const Icon = getEventIcon(event.type);
                    return (
                      <div key={index} className="flex items-center text-sm">
                        <Icon className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="font-medium">{event.time}</span>
                        <span className="ml-2 text-gray-700">{event.title}</span>
                      </div>
                    );
                  })}
                  {getEventsForDate(selectedDate).length === 0 && (
                    <p className="text-sm text-gray-500">No events scheduled for this date</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EditorialCalendar;